// Generated from the Academy platform-foundation contract and transport template. Do not edit.
export const academyErrorCodes = ["VALIDATION_ERROR","AUTHENTICATION_REQUIRED","AUTHORIZATION_DENIED","RESOURCE_NOT_FOUND","CONFLICT","INVALID_STATE_TRANSITION","PRECONDITION_FAILED","CONCURRENCY_CONFLICT","IDEMPOTENCY_CONFLICT","RATE_LIMITED","PROVIDER_FAILURE","TEMPORARILY_UNAVAILABLE","INTERNAL_ERROR"] as const;
export type AcademyErrorCode = (typeof academyErrorCodes)[number];
export interface AcademyFieldError {
  field: string;
  code: string;
  message: string;
}
export class AcademyApiError extends Error {
  readonly code?: AcademyErrorCode;
  readonly correlationId?: string;
  readonly requestId?: string;
  readonly fieldErrors: AcademyFieldError[];
  readonly retryable: boolean;
  readonly retryAfterSeconds?: number;
  readonly details?: { resourceVersion?: number; operationId?: string };
  constructor(
    readonly status: number,
    message: string,
    metadata: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = "AcademyApiError";
    this.code = academyErrorCodes.includes(metadata.code as AcademyErrorCode)
      ? (metadata.code as AcademyErrorCode)
      : undefined;
    this.correlationId =
      typeof metadata.correlationId === "string"
        ? metadata.correlationId
        : undefined;
    this.requestId =
      typeof metadata.requestId === "string" ? metadata.requestId : undefined;
    this.fieldErrors = Array.isArray(metadata.fieldErrors)
      ? metadata.fieldErrors
          .filter(
            (item): item is AcademyFieldError =>
              !!item &&
              typeof item.field === "string" &&
              typeof item.code === "string" &&
              typeof item.message === "string",
          )
          .slice(0, 50)
      : [];
    this.retryable = metadata.retryable === true;
    const retryAfter = Number(metadata.retryAfterSeconds);
    this.retryAfterSeconds =
      Number.isInteger(retryAfter) && retryAfter >= 0 && retryAfter <= 86400
        ? retryAfter
        : undefined;
    if (metadata.details && typeof metadata.details === "object") {
      const detail = metadata.details as Record<string, unknown>;
      this.details = {
        ...(Number.isSafeInteger(detail.resourceVersion)
          ? { resourceVersion: Number(detail.resourceVersion) }
          : {}),
        ...(typeof detail.operationId === "string"
          ? { operationId: detail.operationId }
          : {}),
      };
    }
  }
}

/** One request only. Domain commands own their retry key; transport never retries mutations. */
export async function academyFetch<T>(
  url: string,
  init: RequestInit = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  if (!headers.has("accept")) headers.set("accept", "application/json");
  if (!headers.has("x-correlation-id"))
    headers.set("x-correlation-id", crypto.randomUUID());
  if (!headers.has("x-request-id"))
    headers.set("x-request-id", crypto.randomUUID());
  const timeout = new AbortController();
  const abort = () => timeout.abort(init.signal?.reason);
  if (init.signal?.aborted) abort();
  else init.signal?.addEventListener("abort", abort, { once: true });
  const timer = setTimeout(
    () =>
      timeout.abort(
        new DOMException("Academy request timed out", "TimeoutError"),
      ),
    30000,
  );
  try {
    const response = await fetch(url, {
      ...init,
      headers,
      credentials: "include",
      cache: "no-store",
      signal: timeout.signal,
    });
    const payload: unknown = await response.json().catch(() => null);
    if (!response.ok) {
      const value =
        payload && typeof payload === "object" && !Array.isArray(payload)
          ? (payload as Record<string, unknown>)
          : {};
      const message = Array.isArray(value.message)
        ? value.message.filter((item) => typeof item === "string").join("; ")
        : typeof value.message === "string"
          ? value.message
          : `Academy request failed (${response.status}).`;
      throw new AcademyApiError(response.status, message, {
        ...value,
        correlationId:
          value.correlationId ??
          response.headers.get("x-correlation-id") ??
          headers.get("x-correlation-id"),
        requestId: value.requestId ?? response.headers.get("x-request-id"),
        retryAfterSeconds:
          value.retryAfterSeconds ??
          response.headers.get("retry-after") ??
          undefined,
      });
    }
    return payload as T;
  } finally {
    clearTimeout(timer);
    init.signal?.removeEventListener("abort", abort);
  }
}
